import React, { useEffect, useMemo, useState } from 'react'
import type { ChangeEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Option, SelectList } from '@indeed/ifl-components'

import { fetchEmployerFilteredVenuesList } from 'syft-acp-core/actions/employer-venues-filtered'
import { selectEmployerVenueOptionsById } from 'syft-acp-core/store/employerVenuesFiltered/selectors'
import { setFilter } from 'syft-acp-core/store/filters/actions'
import { toLocationOptions, toWordPrefixPatterns } from 'syft-acp-util/locationSearch'

import type { EmployerAccountsVenueFilterProps } from './EmployerAccountsList.types'

export const VENUE_FILTER_NAME = 'venue_id'

const ALL_VENUES = 'all-venues'

// Large employers have thousands of venues (OCS ~8,000); rendering them all makes the popup sluggish,
// so only the first matches are listed and the rest are reached by typing.
export const MAX_LISTED_VENUES = 100

// The popup is portaled to <body>, but ACP's page body (`.App .content`) is its own stacking context
// at z-index 5, so IFL's default popup z-index (1) renders underneath the accounts table. 1000 is the
// layer Bootstrap dropdowns use on the same page: above page content, below the fixed nav and modals.
export const POPUP_Z_INDEX = 1000

// Sized to sit alongside the legacy Bootstrap Name and E-mail inputs (34px high, 14px text).
const compactText = { fontSize: 2 }

/**
 * Venue filter for the employer Accounts tab. Selecting a venue sets `venue_id` in the
 * URL query; choosing "All venues" removes it.
 *
 * Options come from the employer's own venue result set rather than the shared
 * entity map, which accumulates the venues of every employer visited this session.
 */
export const EmployerAccountsVenueFilter = ({ employerID }: EmployerAccountsVenueFilterProps) => {
  const dispatch = useDispatch()
  const venues = useSelector(state => selectEmployerVenueOptionsById(state, employerID))
  const value = useSelector(
    state => state.routing?.locationBeforeTransitions?.query?.[VENUE_FILTER_NAME] as string | undefined,
  )
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (employerID) dispatch(fetchEmployerFilteredVenuesList(employerID))
  }, [dispatch, employerID])

  const options = useMemo(() => toLocationOptions(venues), [venues])
  const matches = useMemo(() => {
    const patterns = toWordPrefixPatterns(search)

    return patterns.length
      ? options.filter(option => patterns.every(p => p.test(option.searchText)))
      : options
  }, [options, search])
  const hiddenMatches = matches.length - MAX_LISTED_VENUES

  const selectedLabel = (selectedValues: string[]) =>
    options.find(option => String(option.id) === selectedValues[0])?.label ?? `Venue ${selectedValues[0]}`

  const handleSelectionChange = ([selected]: string[]) =>
    dispatch(setFilter(VENUE_FILTER_NAME, selected && selected !== ALL_VENUES ? selected : null))

  return (
    <Box sx={{ inlineSize: '340px' }}>
      <SelectList
        aria-label="Filter accounts by venue"
        placeholder="All venues"
        size="sm"
        sx={{ ...compactText, borderRadius: 'sm' }}
        filterable
        portaledPopup
        popupProps={{ sx: { zIndex: POPUP_Z_INDEX, inlineSize: '30rem', maxInlineSize: '90vw' } }}
        filterProps={{
          'aria-label': 'Search venues by name or ID',
          placeholder: 'Search by name or ID',
          value: search,
          onChange: (event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value),
        }}
        selectedValues={value ? [value] : []}
        setSelectionDisplayLabel={selectedLabel}
        onSelectionChange={handleSelectionChange}
        onOpenChange={isOpen => {
          if (!isOpen) setSearch('')
        }}
      >
        {!search && (
          <Option value={ALL_VENUES} sx={compactText}>
            All venues
          </Option>
        )}
        {matches.slice(0, MAX_LISTED_VENUES).map(option => (
          <Option key={option.id} value={String(option.id)} sx={compactText}>
            {option.label}
          </Option>
        ))}
        {matches.length === 0 && (
          <Option value="no-matches" disabled sx={compactText}>
            No matches.
          </Option>
        )}
        {hiddenMatches > 0 && (
          <Option value="more-matches" disabled sx={compactText}>
            {`${hiddenMatches.toLocaleString('en-GB')} more. Keep typing to narrow the list.`}
          </Option>
        )}
      </SelectList>
    </Box>
  )
}
